/*
  Dhrohar collection/search helpers. Dawn's facets.js keeps doing all filtering, sorting, URL and
  history work; this file only:
  1. <dhrohar-scroll-memory>: remembers the scroll position of a listing URL (sessionStorage) and
     restores it when the shopper comes back with the browser Back button after a full page load.
     It ignores in-page facet history (popstate) and bfcache restores, which the browser handles.
  2. Keeps the mobile filter drawer's "Show N results" buttons in step with Dawn's #ProductCount.
  Safe to load more than once and after section reloads in the theme editor.
*/
(() => {
  if (!window.customElements || customElements.get('dhrohar-scroll-memory')) return;

  const PREFIX = 'dhrohar:scroll:';
  const keyFor = () => PREFIX + window.location.pathname + window.location.search;

  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable: nothing to remember */
      }
    },
  };

  const save = () => session.set(keyFor(), String(Math.round(window.scrollY || window.pageYOffset || 0)));

  function navigationType() {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      if (entry) return entry.type;
    } catch (e) {
      /* old browser */
    }
    try {
      return performance.navigation && performance.navigation.type === 2 ? 'back_forward' : 'navigate';
    } catch (e) {
      return 'navigate';
    }
  }

  let listenersAdded = false;
  let restored = false;

  class DhroharScrollMemory extends HTMLElement {
    connectedCallback() {
      if (!listenersAdded) {
        listenersAdded = true;
        window.addEventListener('pagehide', save);
        // Save right before leaving for a product (pagehide can be late on some mobile browsers).
        document.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest && event.target.closest('a[href]');
            if (link && /\/products\//.test(link.getAttribute('href') || '')) save();
          },
          true
        );
      }
      if (restored) return;
      restored = true;
      if (navigationType() !== 'back_forward') return;
      const stored = Number(session.get(keyFor()));
      if (!stored || stored < 1) return;
      const restore = () => {
        if (Math.abs((window.scrollY || 0) - stored) > 40) window.scrollTo(0, stored);
      };
      requestAnimationFrame(() => requestAnimationFrame(restore));
      // Images above the fold can shift the layout before load; try once more.
      window.addEventListener('load', restore, { once: true });
    }
  }
  customElements.define('dhrohar-scroll-memory', DhroharScrollMemory);

  /* "Show N results" buttons follow Dawn's product count after each facet update. */
  function updateApplyButtons() {
    const count = document.getElementById('ProductCount');
    if (!count) return;
    const n = parseInt(count.dataset.productCount, 10);
    if (Number.isNaN(n)) return;
    const label = `Show ${n} result${n === 1 ? '' : 's'}`;
    document.querySelectorAll('[data-dhrohar-apply]').forEach((button) => {
      if (button.textContent.trim() !== label) button.textContent = label;
    });
  }

  let observer = null;
  function watchCount() {
    const count = document.getElementById('ProductCount');
    if (!count || !window.MutationObserver) return;
    if (observer) observer.disconnect();
    observer = new MutationObserver(updateApplyButtons);
    observer.observe(count, { attributes: true, attributeFilter: ['data-product-count'], childList: true, characterData: true, subtree: true });
    updateApplyButtons();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCount);
  else watchCount();
  document.addEventListener('shopify:section:load', watchCount);
})();

/* Price ordering uses Shopify's native price sorts; discount ordering includes every filtered page. */
(() => {
  if (window.dhroharPriceOrdering) return;
  window.dhroharPriceOrdering = true;
  const discountMode = () => new URL(location.href).searchParams.get('dh_sort') === 'discount';
  function navigate(order) {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');
    if (order === 'discount-descending') {
      url.searchParams.set('dh_sort', 'discount');
      url.searchParams.set('sort_by', 'price-ascending');
    } else {
      url.searchParams.delete('dh_sort');
      url.searchParams.set('sort_by', order);
    }
    location.assign(url.href);
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-dh-price-order]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    navigate(link.dataset.dhPriceOrder);
  }, true);
  document.addEventListener('change', (event) => {
    const select = event.target.closest('select[name="sort_by"]');
    if (!select || !location.pathname.includes('/collections/')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    navigate(select.value);
  }, true);

  const processed = new WeakSet();
  let pending = null;
  let scheduled = false;
  function controls() {
    const active = discountMode();
    const order = active ? 'discount-descending' : new URL(location.href).searchParams.get('sort_by');
    document.querySelectorAll('[data-dh-price-order]').forEach((link) => {
      if (link.dataset.dhPriceOrder === order) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (active && select.querySelector('option[value="discount-descending"]')) select.value = 'discount-descending';
    });
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      let input = form.querySelector('input[name="dh_sort"]');
      if (active && !input) {
        input = document.createElement('input');
        input.type = 'hidden'; input.name = 'dh_sort'; input.value = 'discount';
        form.append(input);
      } else if (!active && input) input.remove();
    });
  }
  async function sortDiscounts() {
    controls();
    if (!discountMode()) return;
    const grid = document.getElementById('product-grid');
    if (!grid || !grid.matches('ul') || processed.has(grid)) return;
    processed.add(grid);
    if (pending) pending.abort();
    const controller = new AbortController();
    pending = controller;
    const container = document.getElementById('ProductGridContainer');
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.className = 'dh-price-sort-status';
    status.textContent = 'Loading best discounts…';
    container.prepend(status);
    grid.setAttribute('aria-busy', 'true');
    try {
      const base = new URL(location.href);
      const currentPage = Number(base.searchParams.get('page') || 1);
      let pages = currentPage;
      container.querySelectorAll('a[href]').forEach((link) => {
        const url = new URL(link.href);
        if (url.origin === base.origin && url.pathname === base.pathname) {
          pages = Math.max(pages, Number(url.searchParams.get('page') || 1));
        }
      });
      const cards = new Map();
      const collect = (root) => root.querySelectorAll('#product-grid > li[data-dh-product-id]').forEach((card) => {
        cards.set(card.dataset.dhProductId, card);
      });
      collect(document);
      if (!cards.size) throw new Error('Product sorting data missing');
      const urls = [];
      for (let page = 1; page <= pages; page++) {
        if (page === currentPage) continue;
        const url = new URL(base); url.searchParams.set('page', String(page));
        urls.push(url.href);
      }
      let next = 0;
      await Promise.all(Array.from({ length: Math.min(3, urls.length) }, async () => {
        while (next < urls.length) {
          const url = urls[next++];
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Unable to load collection');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (!doc.querySelector('#product-grid')) throw new Error('Collection missing');
          collect(doc);
        }
      }));
      if (controller.signal.aborted || !grid.isConnected || !discountMode()) return;
      const ordered = [...cards.values()].sort((a, b) =>
        Number(b.dataset.dhDiscount) - Number(a.dataset.dhDiscount) ||
        Number(a.dataset.dhPrice) - Number(b.dataset.dhPrice));
      if (!ordered.length) throw new Error('No products to sort');
      grid.replaceChildren(...ordered);
      container.querySelectorAll('.pagination-wrapper, nav[aria-label="Pagination"]').forEach((nav) => nav.remove());
      status.classList.add('visually-hidden');
      status.textContent = 'Showing all ' + ordered.length + ' products, biggest discounts first.';
    } catch (error) {
      if (error.name !== 'AbortError') status.textContent = 'Could not load best discounts. Please refresh to try again.';
    } finally {
      grid.removeAttribute('aria-busy');
    }
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; sortDiscounts(); });
  }
  function start() {
    if (!location.pathname.includes('/collections/')) return;
    new MutationObserver(schedule).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('popstate', schedule);
})();
